import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { TadManagerConfigUpdateStore } from "../../store";
import { TadAuthProvider } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { IdCardType } from "../../../../api/graphql";
import { InputStringMask } from "../../../common/controls";
import { TadManagerConfigProcedureNumberCustomField } from "../custom/TadManagerConfigProcedureNumberCustomField";
import { TadManagerConfigMiArgentinaPasswordCustomField } from "../custom/TadManagerConfigMiArgentinaPasswordCustomField";
import { TadManagerConfigArcaPasswordCustomField } from "../custom/TadManagerConfigArcaPasswordCustomField";
import { TadManagerConfigAnsesPasswordCustomField } from "../custom/TadManagerConfigAnsesPasswordCustomField";
import { Gender } from "../../../../api/graphql";
import { FormControl } from "@mui/material";

export type TadManagerConfigUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  authProviderOptions: { id: string; displayId: string }[];
  authProviderError?: string;
  authProvider: TadAuthProvider | "";
  onAuthProviderChange?: (value: TadAuthProvider | "") => void;
  idCardTypeOptions: { id: string; displayId: string }[];
  idCardTypeError?: string;
  idCardType: IdCardType | "";
  onIdCardTypeChange?: (value: IdCardType | "") => void;
  idCard: string;
  idCardSelectedValue?: { displayId: string; id: string } | null;
  onIdCardChange?: (value: string) => void;
  idCardError?: string;
  procedureNumber: string;
  procedureNumberSelectedValue?: { displayId: string; id: string } | null;
  onProcedureNumberChange?: (value: string) => void;
  procedureNumberError?: string;
  miArgentinaPassword: string;
  miArgentinaPasswordSelectedValue?: { displayId: string; id: string } | null;
  onMiArgentinaPasswordChange?: (value: string) => void;
  miArgentinaPasswordError?: string;
  arcaPassword: string;
  arcaPasswordSelectedValue?: { displayId: string; id: string } | null;
  onArcaPasswordChange?: (value: string) => void;
  arcaPasswordError?: string;
  ansesPassword: string;
  ansesPasswordSelectedValue?: { displayId: string; id: string } | null;
  onAnsesPasswordChange?: (value: string) => void;
  ansesPasswordError?: string;
  genderOptions: { id: string; displayId: string }[];
  genderError?: string;
  gender: Gender | "";
  onGenderChange?: (value: Gender | "") => void;
  tadManagerConfigUpdateStore: TadManagerConfigUpdateStore;
};

export const TadManagerConfigUpdateForm: React.FC<TadManagerConfigUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      recordId,
      authProviderOptions = [],
      authProviderError,
      authProvider = "" as TadAuthProvider | "",
      onAuthProviderChange = () => {},
      idCardTypeOptions = [],
      idCardTypeError,
      idCardType = "" as IdCardType | "",
      onIdCardTypeChange = () => {},
      idCard = "",
      idCardError,
      onIdCardChange = () => {},
      procedureNumber = "",
      procedureNumberError,
      onProcedureNumberChange = () => {},
      miArgentinaPassword = "",
      miArgentinaPasswordError,
      onMiArgentinaPasswordChange = () => {},
      arcaPassword = "",
      arcaPasswordError,
      onArcaPasswordChange = () => {},
      ansesPassword = "",
      ansesPasswordError,
      onAnsesPasswordChange = () => {},
      genderOptions = [],
      genderError,
      gender = "" as Gender | "",
      onGenderChange = () => {},
      tadManagerConfigUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              recordId={recordId}
              values={{
                authProvider,
                idCardType,
                idCard,
                procedureNumber,
                miArgentinaPassword,
                arcaPassword,
                ansesPassword,
                gender,
              }}
            >
              <FormExtras
                entity="TadManagerConfig"
                mode="update"
                recordId={recordId}
                values={{
                  authProvider,
                  idCardType,
                  idCard,
                  procedureNumber,
                  miArgentinaPassword,
                  arcaPassword,
                  ansesPassword,
                  gender,
                }}
                options={{}}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "authProvider":
                      onAuthProviderChange?.(valor);
                      break;
                    case "idCardType":
                      onIdCardTypeChange?.(valor);
                      break;
                    case "idCard":
                      onIdCardChange?.(valor);
                      break;
                    case "procedureNumber":
                      onProcedureNumberChange?.(valor);
                      break;
                    case "miArgentinaPassword":
                      onMiArgentinaPasswordChange?.(valor);
                      break;
                    case "arcaPassword":
                      onArcaPasswordChange?.(valor);
                      break;
                    case "ansesPassword":
                      onAnsesPasswordChange?.(valor);
                      break;
                    case "gender":
                      onGenderChange?.(valor);
                      break;
                  }
                }}
              />
              <GroupContainer
                id="null"
                errorText={tadManagerConfigUpdateStore.getCardErrors("null")}
                title={langStore.groupTadManagerConfigLabel}
                collapsible={false}
              >
                <FormControl fullWidth>
                  <InputReference
                    id="authProvider"
                    autoFocus
                    disabled={false}
                    error={Boolean(authProviderError)}
                    helperText={
                      authProviderError ||
                      tadManagerConfigUpdateStore.warnings["authProvider"]
                        ?.message
                    }
                    label={langStore.TadManagerConfigAuthProviderLabel}
                    options={authProviderOptions}
                    nullText={langStore.optionalFieldHint}
                    selectedValue={
                      authProvider
                        ? {
                            id: authProvider,
                            displayId:
                              authProviderOptions.find(
                                (o: { id: string }) => o.id === authProvider
                              )?.displayId || authProvider,
                          }
                        : null
                    }
                    onChange={() => {}}
                    onSelect={(
                      value: { id: string; displayId: string } | null
                    ) =>
                      onAuthProviderChange(
                        (value?.id as TadAuthProvider | "") ||
                          ("" as TadAuthProvider | "")
                      )
                    }
                  />
                  <InputReference
                    id="idCardType"
                    disabled={false}
                    error={Boolean(idCardTypeError)}
                    helperText={
                      idCardTypeError ||
                      tadManagerConfigUpdateStore.warnings["idCardType"]
                        ?.message
                    }
                    label={langStore.TadManagerConfigIdCardTypeLabel}
                    options={idCardTypeOptions}
                    nullText={langStore.optionalFieldHint}
                    selectedValue={
                      idCardType
                        ? {
                            id: idCardType,
                            displayId:
                              idCardTypeOptions.find(
                                (o: { id: string }) => o.id === idCardType
                              )?.displayId || idCardType,
                          }
                        : null
                    }
                    onChange={() => {}}
                    onSelect={(
                      value: { id: string; displayId: string } | null
                    ) =>
                      onIdCardTypeChange(
                        (value?.id as IdCardType | "") ||
                          ("" as IdCardType | "")
                      )
                    }
                  />
                  <InputStringMask
                    value={idCard}
                    error={Boolean(idCardError)}
                    warning={
                      !idCardError &&
                      Boolean(tadManagerConfigUpdateStore.warnings["idCard"])
                    }
                    helperText={
                      idCardError ||
                      tadManagerConfigUpdateStore.warnings["idCard"]?.message
                    }
                    label={langStore.TadManagerConfigIdCardLabel}
                    onChange={(value: string) => onIdCardChange(value)}
                    conditionalMasks={[
                      {
                        mask: "999999999",
                        masks: ["999999999", "99999999", "9999999"],
                        when: {
                          field: "idCardType",
                          operator: "in",
                          values: ["dni"],
                        },
                      },
                      {
                        mask: "99-99999999-9",
                        masks: ["99-99999999-9", "99-9999999-9"],
                        when: {
                          field: "idCardType",
                          operator: "in",
                          values: ["cuitCuil"],
                        },
                      },
                    ]}
                    conditionValues={{ idCardType: idCardType }}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TadManagerConfigIdCardDescription}
                  />
                  <TadManagerConfigProcedureNumberCustomField
                    value={procedureNumber}
                    error={Boolean(procedureNumberError)}
                    helperText={
                      procedureNumberError ||
                      tadManagerConfigUpdateStore.warnings["procedureNumber"]
                        ?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TadManagerConfigProcedureNumberLabel}
                    description={
                      langStore.TadManagerConfigProcedureNumberDescription
                    }
                    onChange={(value: string) => onProcedureNumberChange(value)}
                  />
                  <TadManagerConfigMiArgentinaPasswordCustomField
                    value={miArgentinaPassword}
                    error={Boolean(miArgentinaPasswordError)}
                    helperText={
                      miArgentinaPasswordError ||
                      tadManagerConfigUpdateStore.warnings[
                        "miArgentinaPassword"
                      ]?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TadManagerConfigMiArgentinaPasswordLabel}
                    description={
                      langStore.TadManagerConfigMiArgentinaPasswordDescription
                    }
                    onChange={(value: string) =>
                      onMiArgentinaPasswordChange(value)
                    }
                  />
                  <TadManagerConfigArcaPasswordCustomField
                    value={arcaPassword}
                    error={Boolean(arcaPasswordError)}
                    helperText={
                      arcaPasswordError ||
                      tadManagerConfigUpdateStore.warnings["arcaPassword"]
                        ?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TadManagerConfigArcaPasswordLabel}
                    description={
                      langStore.TadManagerConfigArcaPasswordDescription
                    }
                    onChange={(value: string) => onArcaPasswordChange(value)}
                  />
                  <TadManagerConfigAnsesPasswordCustomField
                    value={ansesPassword}
                    error={Boolean(ansesPasswordError)}
                    helperText={
                      ansesPasswordError ||
                      tadManagerConfigUpdateStore.warnings["ansesPassword"]
                        ?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TadManagerConfigAnsesPasswordLabel}
                    description={
                      langStore.TadManagerConfigAnsesPasswordDescription
                    }
                    onChange={(value: string) => onAnsesPasswordChange(value)}
                  />
                  <InputReference
                    id="gender"
                    disabled={false}
                    error={Boolean(genderError)}
                    helperText={
                      genderError ||
                      tadManagerConfigUpdateStore.warnings["gender"]?.message
                    }
                    label={langStore.TadManagerConfigGenderLabel}
                    options={genderOptions}
                    nullText={langStore.optionalFieldHint}
                    selectedValue={
                      gender
                        ? {
                            id: gender,
                            displayId:
                              genderOptions.find(
                                (o: { id: string }) => o.id === gender
                              )?.displayId || gender,
                          }
                        : null
                    }
                    onChange={() => {}}
                    onSelect={(
                      value: { id: string; displayId: string } | null
                    ) =>
                      onGenderChange(
                        (value?.id as Gender | "") || ("" as Gender | "")
                      )
                    }
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
